import Vue from 'vue'
import Vuex from 'vuex'
import { reduxStore } from './redux-store'
import {
  getWebchatRuntime,
  setUserActivityHistoryEntry,
  setWebchatBotConnection,
  setWebchatBotStore,
  subscribeWebchatRuntime,
} from './webchat-runtime'
import {
  container as setStorageContainer,
  registerStorage,
  sas as setStorageSas,
  unregisterStorage,
  uri as setStorageUri,
} from './modules/storage'
import {
  email,
  expireToken,
  generateToken,
  isAuthenticated as setIsAuthenticated,
  jwtToken,
  setTokens,
  tempRedirect,
  tempToken,
  tokenGenerationStatus,
  tokenVerificationStatus,
  tryAuthenticate,
  user,
  verifyToken,
  type GenerateTokenPayload,
  type TokensData,
  type UserRecord,
} from './modules/user'
import {
  CAN_EDIT_ACTIONS,
  CURRENT_ACTION,
  DISABLE_EDIT_ACTIONS,
  SET_JOURNEY_COMPLETE,
  UPDATE_CURRENT_ACTION,
  UPDATE_USER_ACTIVITY_HISTORY,
  conversation,
  postActivity,
  registerActivityCallback,
  registerWebchat,
  sendEvent,
  sendMessage,
  token,
  unregisterWebchat,
  userId,
  userObj,
  type RegisterWebchatPayload,
  type UserObj,
} from './modules/webchat'
import type { ActionHistory, BotConnection, BotFrameworkStore } from './webchat-runtime'
import type { UnknownAction } from '@reduxjs/toolkit'
import type { AppDispatch, RootState } from './redux-store'
import type { BotEventEntry, BotEventsState } from './modules/bot-events'
import type { ProgressPayload } from '@/react-components/components/progress/types'
import {
  closed as botViewClosed,
  type ThemeOptions,
  type BotViewState,
} from './modules/bot-view'

Vue.use(Vuex)

const composeBotEvents = (botEvents: BotEventsState): BotEventsState => ({
  model: { ...botEvents.model },
  overlay: { ...botEvents.overlay },
  theme: { ...botEvents.theme },
  config: { ...botEvents.config },
  screenSize: { ...botEvents.screenSize },
  modalHidden: { ...botEvents.modalHidden },
})

const composeProgress = (progress: ProgressPayload): ProgressPayload => ({
  recommendations: [...progress.recommendations],
  flows: { ...progress.flows },
})

const composeBotView = (botView: BotViewState): BotViewState => ({
  model: { ...botView.model },
  showModal: botView.showModal,
  showOverlay: botView.showOverlay,
  hasOverlayToggle: botView.hasOverlayToggle,
  themeOptions: {
    default: { ...botView.themeOptions.default },
    ...(botView.themeOptions.config !== undefined
      ? { config: { ...botView.themeOptions.config } }
      : {}),
  },
  sendBoxDisabled: botView.sendBoxDisabled,
})

const composeState = () => {
  const reduxState = reduxStore.getState()
  const runtime = getWebchatRuntime()
  return {
    storage: { ...reduxState.storage },
    user: { ...reduxState.user },
    webchat: {
      ...reduxState.webchat,
      botConnection: runtime.botConnection,
      store: runtime.store,
      userActivityHistory: runtime.userActivityHistory,
    },
    botEvents: composeBotEvents(reduxState.botEvents),
    progress: composeProgress(reduxState.progress),
    botView: composeBotView(reduxState.botView),
  }
}

const state = Vue.observable(composeState())

const syncBotEventEntry = <K extends keyof BotEventsState>(
  key: K,
  next: BotEventsState[K],
) => {
  // Only replace when seq advances. Replacing every entry on every Redux
  // dispatch would notify Vue watchers for unrelated events (e.g. theme
  // updates forcing screenSize back to the initial payload 0).
  if (state.botEvents[key].seq !== next.seq) {
    state.botEvents[key] = next
  }
}

const syncObservable = () => {
  const next = composeState()
  Object.assign(state.storage, next.storage)
  Object.assign(state.user, next.user)
  state.user.user = next.user.user
  Object.assign(state.webchat, next.webchat)
  syncBotEventEntry('model', next.botEvents.model)
  syncBotEventEntry('overlay', next.botEvents.overlay)
  syncBotEventEntry('theme', next.botEvents.theme)
  syncBotEventEntry('config', next.botEvents.config)
  syncBotEventEntry('screenSize', next.botEvents.screenSize)
  syncBotEventEntry('modalHidden', next.botEvents.modalHidden)
  // Progress payloads are replaced wholesale in Redux; reuse those
  // references so unrelated dispatches do not notify progress watchers.
  // Assign the progress object atomically so sync watchers never see a
  // half-updated recommendations/flows pair mid-tick.
  const reduxProgress = reduxStore.getState().progress
  if (
    state.progress.recommendations !== reduxProgress.recommendations ||
    state.progress.flows !== reduxProgress.flows
  ) {
    state.progress = {
      recommendations: reduxProgress.recommendations,
      flows: reduxProgress.flows,
    }
  }
  const reduxBotView = reduxStore.getState().botView
  state.botView = composeBotView(reduxBotView)
}

reduxStore.subscribe(syncObservable)
subscribeWebchatRuntime(syncObservable)

const botEventEntry = <T>(entry: BotEventEntry<T>): BotEventEntry<T> => ({
  payload: entry.payload,
  seq: entry.seq,
})

type ShimGetters = {
  'storage/uri': unknown
  'storage/container': unknown
  'storage/sas': unknown
  'storage/isStorageRegistered': boolean
  'user/tokenGenerationStatus': number
  'user/tokenVerificationStatus': number
  'user/tempToken': string | undefined
  'user/tempRedirect': string | undefined
  'user/jwtToken': string | undefined
  'user/isAuthenticated': boolean
  'webchat/botConnection': unknown
  'webchat/conversation': string | undefined
  'webchat/store': unknown
  'webchat/token': string | undefined
  'webchat/userObj': UserObj | undefined
  'webchat/userId': string | undefined
  'webchat/currentAction': unknown
  'webchat/isWebchatRegistered': boolean
  'botEvents/model': BotEventEntry<object>
  'botEvents/overlay': BotEventEntry<object>
  'botEvents/theme': BotEventEntry<object>
  'botEvents/config': BotEventEntry<object>
  'botEvents/screenSize': BotEventEntry<number>
  'botEvents/modalHidden': BotEventEntry<null>
  'progress/payload': ProgressPayload
  'botView/model': Record<string, unknown>
  'botView/showModal': boolean
  'botView/showOverlay': boolean
  'botView/themeOptions': ThemeOptions
}

const getterFns: { [K in keyof ShimGetters]: () => ShimGetters[K] } = {
  'storage/uri': () => state.storage.uri,
  'storage/container': () => state.storage.container,
  'storage/sas': () => state.storage.sas,
  'storage/isStorageRegistered': () =>
    state.storage.uri !== undefined &&
    state.storage.container !== undefined &&
    state.storage.sas !== undefined,
  'user/tokenGenerationStatus': () => state.user.tokenGenerationStatus,
  'user/tokenVerificationStatus': () => state.user.tokenVerificationStatus,
  'user/tempToken': () => state.user.tempToken,
  'user/tempRedirect': () => state.user.tempRedirect,
  'user/jwtToken': () => state.user.jwtToken,
  'user/isAuthenticated': () =>
    Boolean(
      state.user.isAuthenticated &&
        state.webchat.conversation !== undefined &&
        state.webchat.userId !== undefined &&
        state.webchat.token !== undefined &&
        state.storage.uri !== undefined &&
        state.storage.container !== undefined &&
        state.storage.sas !== undefined,
    ),
  'webchat/botConnection': () => state.webchat.botConnection,
  'webchat/conversation': () => state.webchat.conversation,
  'webchat/store': () => state.webchat.store,
  'webchat/token': () => state.webchat.token,
  'webchat/userObj': () => state.webchat.userObj,
  'webchat/userId': () => state.webchat.userId,
  'webchat/currentAction': () => state.webchat.currentAction,
  'webchat/isWebchatRegistered': () =>
    state.webchat.botConnection !== undefined &&
    state.webchat.conversation !== undefined &&
    state.webchat.store !== undefined &&
    state.webchat.token !== undefined &&
    state.webchat.userId !== undefined,
  'botEvents/model': () => botEventEntry(state.botEvents.model),
  'botEvents/overlay': () => botEventEntry(state.botEvents.overlay),
  'botEvents/theme': () => botEventEntry(state.botEvents.theme),
  'botEvents/config': () => botEventEntry(state.botEvents.config),
  'botEvents/screenSize': () => botEventEntry(state.botEvents.screenSize),
  'botEvents/modalHidden': () => botEventEntry(state.botEvents.modalHidden),
  'progress/payload': () => ({
    recommendations: state.progress.recommendations,
    flows: state.progress.flows,
  }),
  'botView/model': () => ({ ...state.botView.model }),
  'botView/showModal': () => state.botView.showModal,
  'botView/showOverlay': () => state.botView.showOverlay,
  'botView/themeOptions': () => ({
    default: { ...state.botView.themeOptions.default },
    ...(state.botView.themeOptions.config !== undefined
      ? { config: { ...state.botView.themeOptions.config } }
      : {}),
  }),
}

const getters: ShimGetters = Object.create(null)
;(Object.keys(getterFns) as Array<keyof ShimGetters>).forEach(name => {
  Object.defineProperty(getters, name, {
    enumerable: true,
    get: getterFns[name],
  })
})

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

const isStorageDetails = (
  payload: unknown,
): payload is { uri: unknown; container: unknown; sas: unknown } => isRecord(payload)

const isGenerateTokenPayload = (payload: unknown): payload is GenerateTokenPayload =>
  isRecord(payload) && typeof payload.email === 'string'

const isTokensData = (payload: unknown): payload is TokensData =>
  isRecord(payload) &&
  isRecord(payload.api) &&
  typeof payload.api.token === 'string' &&
  isRecord(payload.blob) &&
  isRecord(payload.directLine) &&
  isRecord(payload.user)

const isRegisterWebchatPayload = (payload: unknown): payload is RegisterWebchatPayload =>
  isRecord(payload) &&
  isRecord(payload.directLine) &&
  typeof payload.directLine.token === 'string' &&
  typeof payload.directLine.conversationId === 'string' &&
  typeof payload.directLine.user === 'string' &&
  isRecord(payload.api) &&
  typeof payload.api.token === 'string' &&
  isUserObj(payload.user)

const isMessagePayload = (
  payload: unknown,
): payload is { message: string; [key: string]: unknown } =>
  isRecord(payload) && typeof payload.message === 'string'

const isActivityCallback = (payload: unknown): payload is (activity: unknown) => void =>
  typeof payload === 'function'

const isActionHistory = (payload: unknown): payload is ActionHistory =>
  isRecord(payload) &&
  typeof payload.actionId === 'string' &&
  typeof payload.isEditableAction === 'boolean'

const isUserObj = (payload: unknown): payload is UserObj =>
  isRecord(payload) &&
  typeof payload.id === 'string' &&
  typeof payload.applicationId === 'string' &&
  typeof payload.customerId === 'string'

const isUserRecord = (payload: unknown): payload is UserRecord => isRecord(payload)

const isBotConnection = (payload: unknown): payload is BotConnection =>
  isRecord(payload) && typeof payload.postActivity === 'function'

const isBotFrameworkStore = (payload: unknown): payload is BotFrameworkStore =>
  isRecord(payload) &&
  typeof payload.dispatch === 'function' &&
  typeof payload.getState === 'function' &&
  typeof payload.subscribe === 'function'

const isUserActivityHistoryPayload = (
  payload: unknown,
): payload is { id: string; payload: ActionHistory | undefined } =>
  isRecord(payload) && typeof payload.id === 'string'

const isUnknownAction = (value: unknown): value is UnknownAction =>
  isRecord(value) && typeof value.type === 'string'

const isThunk = (
  value: unknown,
): value is (dispatch: AppDispatch, getState: () => RootState) => unknown =>
  typeof value === 'function'

const dispatchToRedux = (value: unknown) => {
  if (isThunk(value)) {
    return reduxStore.dispatch(value)
  }
  if (isUnknownAction(value)) {
    return reduxStore.dispatch(value)
  }
  return value
}

const requirePayload = <T>(
  payload: unknown,
  guard: (value: unknown) => value is T,
  type: string,
): T => {
  if (!guard(payload)) {
    throw new Error(`Invalid payload for ${type}`)
  }
  return payload
}

const actionByType = (type: string, payload: unknown) => {
  switch (type) {
    case 'storage/registerStorage':
      return registerStorage(requirePayload(payload, isStorageDetails, type))
    case 'storage/unregisterStorage':
      return unregisterStorage()
    case 'user/expireToken':
      return expireToken()
    case 'user/generateToken':
      return generateToken(requirePayload(payload, isGenerateTokenPayload, type))
    case 'user/verifyToken':
      return verifyToken(typeof payload === 'string' ? payload : undefined)
    case 'user/setTokens':
      return setTokens(requirePayload(payload, isTokensData, type))
    case 'user/tryAuthenticate':
      return tryAuthenticate()
    case 'webchat/registerWebchat':
      return registerWebchat(requirePayload(payload, isRegisterWebchatPayload, type))
    case 'webchat/registerActivityCallback':
      return registerActivityCallback(requirePayload(payload, isActivityCallback, type))
    case 'webchat/sendEvent':
      return sendEvent(payload)
    case 'webchat/sendMessage':
      return sendMessage(requirePayload(payload, isMessagePayload, type))
    case 'webchat/postActivity':
      return postActivity(payload)
    case 'webchat/unregisterWebchat':
      return unregisterWebchat()
    case 'webchat/SET_JOURNEY_COMPLETE':
      return SET_JOURNEY_COMPLETE()
    case 'webchat/UPDATE_CURRENT_ACTION':
      return UPDATE_CURRENT_ACTION(payload)
    case 'webchat/UPDATE_USER_ACTIVITY_HISTORY':
      return UPDATE_USER_ACTIVITY_HISTORY(
        requirePayload(payload, (value): value is string => typeof value === 'string', type),
      )
    case 'webchat/DISABLE_EDIT_ACTIONS':
      return DISABLE_EDIT_ACTIONS()
    case 'botView/closed':
      return botViewClosed()
    default:
      return undefined
  }
}

const mutationByType = (type: string, payload: unknown) => {
  switch (type) {
    case 'storage/uri':
      return setStorageUri(payload)
    case 'storage/container':
      return setStorageContainer(payload)
    case 'storage/sas':
      return setStorageSas(payload)
    case 'storage/registerStorage':
      return registerStorage(requirePayload(payload, isStorageDetails, type))
    case 'storage/unregisterStorage':
      return unregisterStorage()
    case 'user/email':
      return email(requirePayload(payload, (value): value is string => typeof value === 'string', type))
    case 'user/tokenGenerationStatus':
    case 'user/tokenVerificationStatus':
      return type === 'user/tokenGenerationStatus'
        ? tokenGenerationStatus(
            requirePayload(payload, (value): value is number => typeof value === 'number', type),
          )
        : tokenVerificationStatus(
            requirePayload(payload, (value): value is number => typeof value === 'number', type),
          )
    case 'user/isAuthenticated':
      return setIsAuthenticated(
        requirePayload(payload, (value): value is boolean => typeof value === 'boolean', type),
      )
    case 'user/tempToken':
    case 'user/jwtToken':
    case 'user/tempRedirect': {
      const value =
        payload === undefined
          ? undefined
          : requirePayload(payload, (candidate): candidate is string => typeof candidate === 'string', type)
      if (type === 'user/tempToken') return tempToken(value)
      if (type === 'user/jwtToken') return jwtToken(value)
      return tempRedirect(value)
    }
    case 'user/user':
      return user(requirePayload(payload, isUserRecord, type))
    case 'webchat/conversation':
    case 'webchat/token':
    case 'webchat/userId': {
      const value =
        payload === undefined
          ? undefined
          : requirePayload(payload, (candidate): candidate is string => typeof candidate === 'string', type)
      if (type === 'webchat/conversation') return conversation(value)
      if (type === 'webchat/token') return token(value)
      return userId(value)
    }
    case 'webchat/userObj':
      return userObj(
        payload === undefined ? undefined : requirePayload(payload, isUserObj, type),
      )
    case 'webchat/CURRENT_ACTION':
      return CURRENT_ACTION(
        payload === undefined ? undefined : requirePayload(payload, isActionHistory, type),
      )
    case 'webchat/CAN_EDIT_ACTIONS':
      return CAN_EDIT_ACTIONS(
        requirePayload(payload, (value): value is boolean => typeof value === 'boolean', type),
      )
    case 'webchat/botConnection':
      setWebchatBotConnection(
        payload === undefined ? undefined : requirePayload(payload, isBotConnection, type),
      )
      return undefined
    case 'webchat/store':
      setWebchatBotStore(
        payload === undefined ? undefined : requirePayload(payload, isBotFrameworkStore, type),
      )
      return undefined
    case 'webchat/USER_ACTIVITY_HISTORY': {
      const historyPayload = requirePayload(payload, isUserActivityHistoryPayload, type)
      setUserActivityHistoryEntry(historyPayload.id, historyPayload.payload)
      return undefined
    }
    default:
      throw new Error(`Unknown mutation: ${type}`)
  }
}

const resolveTypeAndPayload = (
  typeOrAction: string | { type: string; payload?: unknown },
  payload?: unknown,
) => {
  if (typeof typeOrAction === 'string') {
    return { type: typeOrAction, payload }
  }
  return {
    type: typeOrAction.type,
    payload: typeOrAction.payload !== undefined ? typeOrAction.payload : payload,
  }
}

const dispatch = (
  typeOrAction: string | { type: string; payload?: unknown },
  payload?: unknown,
) => {
  const resolved = resolveTypeAndPayload(typeOrAction, payload)
  const created = actionByType(resolved.type, resolved.payload)
  if (created === undefined) {
    return Promise.reject(new Error(`Unknown action: ${resolved.type}`))
  }
  return Promise.resolve(dispatchToRedux(created))
}

const commit = (
  typeOrAction: string | { type: string; payload?: unknown },
  payload?: unknown,
) => {
  const resolved = resolveTypeAndPayload(typeOrAction, payload)
  const action = mutationByType(resolved.type, resolved.payload)
  if (isUnknownAction(action)) {
    dispatchToRedux(action)
  }
}

const store = {
  state,
  getters,
  dispatch,
  commit,
}

export { reduxStore }
export default store
